import { useState } from 'react'
import { useQuotes, QUOTE_STATUS_CONFIG } from '../../api/sales'
import { Link } from 'react-router-dom'
import { FileText, Search } from 'lucide-react'

type QuoteStatusFilter = 'all' | 'negotiating' | 'awaiting_confirm' | 'confirmed' | 'rejected'

const statusFilters: Array<{ value: QuoteStatusFilter; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'negotiating', label: '洽谈中' },
  { value: 'awaiting_confirm', label: '待确认' },
  { value: 'confirmed', label: '已确认' },
  { value: 'rejected', label: '已拒绝' },
]

export default function QuoteListPage() {
  const [filter, setFilter] = useState<QuoteStatusFilter>('all')
  const [page, setPage] = useState(0)
  const [inputKeyword, setInputKeyword] = useState('')
  const [keyword, setKeyword] = useState('')

  const filteredQuotes = useQuotes(filter, page, 20, keyword)

  const handleStatusFilter = (newFilter: QuoteStatusFilter) => {
    setFilter(newFilter)
    setPage(0)
  }

  const handleSearch = () => {
    setKeyword(inputKeyword.trim())
    setPage(0)
  }

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      negotiating: '洽谈中',
      awaiting_confirm: '待确认',
      confirmed: '已确认',
      rejected: '已拒绝',
      cancelled: '已作废',
      draft: '草稿',
    }
    return statusMap[status] || status
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">报价单管理</h1>
        <p className="mt-1 text-sm text-gray-500">管理所有销售报价单和洽谈记录</p>
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {statusFilters.map((sf) => (
              <button
                key={sf.value}
                onClick={() => handleStatusFilter(sf.value)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
                  filter === sf.value
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {sf.label}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="搜索报价单、医院、设备..."
                value={inputKeyword}
                onChange={(e) => setInputKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="rounded-lg bg-primary-500 px-4 py-2 text-sm text-white transition hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">报价单号</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">工单号</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">医院</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">设备</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">故障描述</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">金额</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">状态</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">销售</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">更新时间</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotes.data?.content.map((quote) => (
                <tr key={quote.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <Link
                      to={`/sales/quotes/${quote.id}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {quote.quoteNo || '-'}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{quote.workOrderNo}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{quote.hospitalName}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {quote.deviceName}
                    {quote.deviceModel ? <span className="text-gray-400"> · {quote.deviceModel}</span> : null}
                  </td>
                  <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-600">
                    {quote.faultSummary}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      ¥{quote.totalAmount.toLocaleString()}
                    </div>
                    {quote.originalPrice && quote.originalPrice !== quote.totalAmount ? (
                      <div className="text-xs text-gray-400 line-through">
                        ¥{quote.originalPrice.toLocaleString()}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium"
                      style={{
                        color:
                          QUOTE_STATUS_CONFIG[quote.status as keyof typeof QUOTE_STATUS_CONFIG]?.color,
                        backgroundColor:
                          QUOTE_STATUS_CONFIG[quote.status as keyof typeof QUOTE_STATUS_CONFIG]
                            ?.bgColor,
                      }}
                    >
                      {getStatusLabel(quote.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{quote.salesName || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(quote.updatedAt).toLocaleDateString('zh-CN')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/sales/quotes/${quote.id}`}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        查看详情
                      </Link>
                      <span className="text-gray-300">|</span>
                      <Link
                        to={`/sales/negotiations/${quote.id}`}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        洽谈记录
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredQuotes.data?.content.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <FileText className="h-12 w-12 text-gray-300" />
                      <span>暂无报价单数据</span>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {filteredQuotes.data && filteredQuotes.data.totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
            <span className="text-sm text-gray-500">共 {filteredQuotes.data.totalElements} 条记录</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((current) => Math.max(0, current - 1))}
                disabled={page === 0}
                className="rounded border border-gray-200 px-3 py-1 hover:bg-gray-50 disabled:opacity-50"
              >
                上一页
              </button>
              <span className="text-sm text-gray-600">
                第 {page + 1} / {filteredQuotes.data.totalPages} 页
              </span>
              <button
                onClick={() =>
                  setPage((current) => Math.min(current + 1, filteredQuotes.data.totalPages - 1))
                }
                disabled={page >= filteredQuotes.data.totalPages - 1}
                className="rounded border border-gray-200 px-3 py-1 hover:bg-gray-50 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
